import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
/**
 * PracticeSession — Core practice/assessment session orchestrator.
 *
 * DDD co-locatie: moved from components/practice/ in s69.
 * Optimized: extracted 7 full-page states + exit modal → practiceSessionStates.tsx (~200r).
 * Fluency feedback states extracted → FluencyFeedbackStates.tsx.
 */
import { motion, AnimatePresence } from 'framer-motion';
import { type Question, type ModuleId, getQuestionTypeConfig, getLearningGoal, getFamilyDefinition, getAllQuestions, getAllowedTiersForGroup, MODULES, getModuleHexColor } from '../../content';
import type { FamilyMasteryState } from '../../mastery';
import { getAdaptiveSessionV11 } from '../../mastery';
import { Button } from '../../components/ui/Button';
import { QuestionRendererFactory } from './renderers/QuestionRendererFactory';
import { FeedbackDisplay } from './feedback/FeedbackDisplay';
import { PracticeHeaderMinimal } from './PracticeHeaderMinimal';
import { QuestionCardClean } from './QuestionCardClean';
import { ComboIndicator } from '../../components/shared/ComboIndicator';
import { CheatSheetDrawer } from './CheatSheetDrawer';
import { SessionSummary } from './SessionSummary';
import { SessionLoadingState, SessionErrorState, SessionEmptyNoContent, SessionEmptyAllMastered, SessionEmptyFallback, SessionSafetyError, ExitConfirmModal } from './practiceSessionStates';
import { FluencyCorrectState, FluencyIncorrectState } from './FluencyFeedbackStates';
import { getMotivationalMessage, getStreakMessage, getLevelTitle } from '../../utils/motivationalMessages';
import { useAppStore } from '../../app/hooks/useAppStore';
import { SESSION_CONFIG, XP_REWARDS, calculateXP } from '../../config/constants';
import { ArrowRight } from 'lucide-react';
import { validateSessionAvailability } from '../logic/sessionValidation';
import { debug, warn, error as logError } from '../../utils/logger';
import { useEscapeKey } from '../../app/hooks/useEscapeKey';
import { DjayAvatar, useDjaySelection } from '../../mascot';
import { createMasteryQueries } from '../../accounts/hooks/useLearnerActions';
import { isFastResponse } from '../logic/questionTimeBaselines';
import { getTodayChallenge } from '../../utils/dailyChallenge';
import { filterSession, evaluateQuestion } from '../logic/pedagogicalGuard';
import { V11_CONFIG } from '../../mastery/selection/selectionConfig';
// ── Types ────────────────────────────────────────────────────
interface PracticeSessionProps {
  moduleId: ModuleId | null;
  questions: Question[];
  onComplete: (summary?: {
    answeredCount: number;
  }) => void;
  onExit: () => void;
  onAnswer: (questionId: string, isCorrect: boolean, userChoice: number, payload?: string, errorType?: string, delta?: number, timeMs?: number) => void;
  cheatSheetViewed: boolean;
  onCheatSheetView: () => void;
  isCrossModule?: boolean;
  isFluency?: boolean;
  labMode?: string;
  pendingLevelUp?: number | null;
  xp?: number;
  onClearPendingLevelUp?: () => void;
}
type Phase = 'question' | 'feedback' | 'summary' | 'fluency-correct' | 'fluency-incorrect';
interface SessionAnswer {
  questionId: string;
  isCorrect: boolean;
  familyId?: string;
  questionType: string;
  errorType?: string;
  timeMs?: number;
}
// ── Helpers ──────────────────────────────────────────────────
function isInteractiveQuestion(question?: Question): boolean {
  if (!question) return false;
  return getQuestionTypeConfig(question.questionType || 'multiple_choice').isInteractive;
}
function getResetState() {
  return {
    currentQuestionIndex: 0,
    phase: 'question' as Phase,
    selectedOption: null as number | null,
    userAnswerIndex: null as number | null,
    userTypedAnswer: '',
    actualIsCorrect: null as boolean | null,
    interactiveReady: false,
    shouldValidateInteractive: false,
    currentStreak: 0,
    totalXpGained: 0
  };
}
// ── Component ────────────────────────────────────────────────
export function PracticeSession({
  moduleId,
  questions,
  onComplete,
  onExit,
  onAnswer,
  cheatSheetViewed,
  onCheatSheetView,
  isCrossModule = false,
  isFluency = false,
  labMode,
  pendingLevelUp = null,
  xp = 0,
  onClearPendingLevelUp
}: PracticeSessionProps) {
  const {
    state,
    selectedGroup,
    groupVersion,
    isInitialized,
    manualPeriod,
    updateDailyChallengeProgress
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  // Mode flags
  const isChallenge = labMode === 'challenge';
  const showFeedback = true;
  const showStreak = true;
  const showCheatSheet = !isCrossModule;
  // Session state
  const [sessionQuestions, setSessionQuestions] = useState<Question[]>([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [phase, setPhase] = useState<Phase>('question');
  const [sessionInitialized, setSessionInitialized] = useState(false);
  const [initError, setInitError] = useState<string | null>(null);
  const lastGroupVersionRef = useRef(groupVersion);
  // Answer state
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  const [actualIsCorrect, setActualIsCorrect] = useState<boolean | null>(null);
  const [userTypedAnswer, setUserTypedAnswer] = useState<string>('');
  const [userAnswerIndex, setUserAnswerIndex] = useState<number | null>(null);
  const [interactiveReady, setInteractiveReady] = useState(false);
  const [shouldValidateInteractive, setShouldValidateInteractive] = useState(false);
  // UI state
  const [isCheatSheetOpen, setIsCheatSheetOpen] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  // Stats
  const [currentStreak, setCurrentStreak] = useState(0);
  const [speedStreak, setSpeedStreak] = useState(0);
  const [motivationalText, setMotivationalText] = useState('');
  const [xpGained, setXpGained] = useState(0);
  const [totalXpGained, setTotalXpGained] = useState(0);
  // Celebration state
  const [streakMilestone, setStreakMilestone] = useState<{
    count: number;
    message: string;
  } | null>(null);
  const [dailyGoalReached, setDailyGoalReached] = useState(false);
  // Session tracking for summary
  const [sessionAnswers, setSessionAnswers] = useState<SessionAnswer[]>([]);
  const [preFamilyMastery, setPreFamilyMastery] = useState<Record<string, FamilyMasteryState>>({});
  const questionStartTimeRef = useRef<number>(Date.now());
  const autoAdvanceTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const answerProcessedRef = useRef(false);
  // Fluency elapsed timer
  const [elapsedMs, setElapsedMs] = useState(0);
  // Challenge adaptivity
  const challengeConsecutiveErrorsRef = useRef(0);
  const [challengeTierDowngraded, setChallengeTierDowngraded] = useState(false);
  const challengeTimerRef = useRef<ReturnType<typeof setTimeout>>();
  // Cheat sheet nudge
  const [cheatSheetNudge, setCheatSheetNudge] = useState(false);
  const nudgeFiredRef = useRef(false);
  const consecutiveErrorsRef = useRef(0);
  const submitAnswerRef = useRef<() => void>(() => {});
  // ── P-12: Frustration detection (per-family consecutive errors) ───
  const familyErrorStreaksRef = useRef<Map<string, number>>(new Map());
  // ── P-13: Mid-session difficulty adjustment ───────────────
  const lastDifficultyAdjustIndexRef = useRef<number>(-999);
  // ── Reset helper ─────────────────────────────────────────
  const resetSession = () => {
    const r = getResetState();
    setSessionQuestions([]);
    setSessionInitialized(false);
    setCurrentQuestionIndex(r.currentQuestionIndex);
    setPhase(r.phase);
    setSelectedOption(r.selectedOption);
    setUserAnswerIndex(r.userAnswerIndex);
    setUserTypedAnswer(r.userTypedAnswer);
    setActualIsCorrect(r.actualIsCorrect);
    setInteractiveReady(r.interactiveReady);
    setShouldValidateInteractive(r.shouldValidateInteractive);
    setCurrentStreak(r.currentStreak);
    setSpeedStreak(0);
    setSessionAnswers([]);
    setTotalXpGained(r.totalXpGained);
    setCheatSheetNudge(false);
    nudgeFiredRef.current = false;
    consecutiveErrorsRef.current = 0;
    challengeConsecutiveErrorsRef.current = 0;
    familyErrorStreaksRef.current.clear();
    setChallengeTierDowngraded(false);
    clearTimeout(autoAdvanceTimerRef.current);
    clearTimeout(challengeTimerRef.current);
    answerProcessedRef.current = false;
  };
  // ── Cleanup timers on unmount ────────────────────────────
  useEffect(() => {
    return () => {
      clearTimeout(autoAdvanceTimerRef.current);
      clearTimeout(challengeTimerRef.current);
    };
  }, []);
  // ── Escape key ───────────────────────────────────────────
  const handleEscapePress = useCallback(() => {
    onExit();
  }, [onExit]);
  useEscapeKey(handleEscapePress, !isCheatSheetOpen);
  // ── Session initialization (adaptive engine) ─────────────
  useEffect(() => {
    if (!isInitialized) return;
    if (lastGroupVersionRef.current !== groupVersion) {
      lastGroupVersionRef.current = groupVersion;
      resetSession();
      debug('Session fully reset due to group change');
    }
    if (!sessionInitialized) {
      setInitError(null);
      if (state.familyMastery) {
        try {
          setPreFamilyMastery(JSON.parse(JSON.stringify(state.familyMastery)));
        } catch (err) {
          logError('Failed to clone familyMastery:', err);
          setPreFamilyMastery({});
        }
      }
      if (isCrossModule) {
        setSessionQuestions(questions);
        setSessionInitialized(true);
      } else if (moduleId) {
        try {
          const familyMastery = state.familyMastery ?? {};
          const questionStates = state.questionStates ?? {};
          const recentFamilies = Object.values(familyMastery).filter(fm => fm && (fm.lastSeen ?? 0) > 0).sort((a, b) => (b.lastSeen ?? 0) - (a.lastSeen ?? 0)).slice(0, 5).map(fm => fm.familyId).filter(id => id && id.length > 0);
          const recentQuestions = Object.entries(questionStates).filter(([_, qs]) => qs && (qs.lastAttemptDate ?? 0) > 0).sort((a, b) => (b[1]?.lastAttemptDate ?? 0) - (a[1]?.lastAttemptDate ?? 0)).slice(0, 10).map(([qId]) => qId.replace(/_g\d+$/, '')).filter(qId => qId && qId.length > 0);
          debug(`v11.2: Generating session for ${moduleId} | Group ${selectedGroup} | Families tracked: ${Object.keys(familyMastery).length}`);
          const queries = createMasteryQueries(state, selectedGroup, manualPeriod);
          const startingTier = queries.getStartingLevel(moduleId, selectedGroup);
          const adaptiveSession = getAdaptiveSessionV11(moduleId, familyMastery, selectedGroup, questionStates, SESSION_CONFIG.QUESTIONS_PER_SESSION, recentFamilies, recentQuestions, undefined, startingTier);
          if (adaptiveSession.length === 0) {
            warn('Empty session generated - all content mastered or unavailable');
            setInitError('empty_session');
          }
          // ── Pedagogical Guard (PEDAGOGICAL_CONTRACT_SPEC §5) ──
          // Filter session through pedagogical contract checks.
          // Fatal violations are replaced from the module's question pool;
          // soft violations pass through but are logged for governance.
          const allModuleQuestions = getAllQuestions().filter(q => q.module === moduleId);
          const {
            questions: guardedQuestions,
            stats: guardStats
          } = filterSession(adaptiveSession, SESSION_CONFIG.QUESTIONS_PER_SESSION, allModuleQuestions);
          if (guardStats.fatalRejected > 0 || guardStats.softFailed > 0) {
            debug(`[PedagogicalGuard] Session filtered: ${guardStats.passed} passed, ${guardStats.softFailed} soft, ${guardStats.fatalRejected} fatal (${guardStats.replaced} replaced)`);
          }
          setSessionQuestions(guardedQuestions);
          setSessionInitialized(true);
          debug(`Session: ${guardedQuestions.length} questions for ${moduleId} (group ${selectedGroup}, engine: v11.2, guard: ${guardStats.fatalRejected}F/${guardStats.softFailed}S)`);
        } catch (error) {
          logError('Failed to generate session:', error);
          setInitError('generation_failed');
          setSessionQuestions([]);
          setSessionInitialized(true);
        }
      }
    }
  }, [moduleId, isCrossModule, questions, sessionInitialized, selectedGroup, groupVersion, isInitialized, state.familyMastery, state.questionStates]);
  const currentQuestion = sessionQuestions[currentQuestionIndex];
  const isLastQuestion = currentQuestionIndex === sessionQuestions.length - 1;
  // Reset answer state when question changes
  useEffect(() => {
    if (!currentQuestion) return;
    setSelectedOption(null);
    setUserAnswerIndex(null);
    setUserTypedAnswer('');
    setInteractiveReady(false);
    setShouldValidateInteractive(false);
    questionStartTimeRef.current = Date.now();
    answerProcessedRef.current = false;
  }, [currentQuestion]);
  // Fluency elapsed timer
  useEffect(() => {
    if (!isFluency || phase !== 'question') {
      setElapsedMs(0);
      return;
    }
    const interval = setInterval(() => {
      setElapsedMs(Date.now() - questionStartTimeRef.current);
    }, 100);
    return () => clearInterval(interval);
  }, [isFluency, phase, currentQuestionIndex]);
  // Enter key handler
  useEffect(() => {
    if (!currentQuestion) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (phase !== 'question' || isCheatSheetOpen) return;
      if (e.key === 'Enter') {
        const interactive = isInteractiveQuestion(currentQuestion);
        if (interactive && interactiveReady) submitAnswerRef.current();else if (!interactive && userAnswerIndex !== null) submitAnswerRef.current();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [phase, isCheatSheetOpen, userAnswerIndex, interactiveReady, currentQuestion]);
  // ── Early returns (full-page states) ─────────────────────
  if (!isInitialized || !sessionInitialized) return <SessionLoadingState data-id="element-8288" />;
  if (initError === 'generation_failed') return <SessionErrorState onExit={onExit} data-id="element-8289" />;
  if (sessionInitialized && sessionQuestions.length === 0) {
    const validationReport = moduleId ? validateSessionAvailability(moduleId, selectedGroup) : null;
    const hasContent = validationReport?.isValid ?? false;
    const hasAnyProgress = state.familyMastery && Object.keys(state.familyMastery).some(fId => state.familyMastery![fId].attempts > 0);
    if (!hasContent) return <SessionEmptyNoContent onExit={onExit} data-id="element-8290" />;
    if (hasAnyProgress) return <SessionEmptyAllMastered selectedGroup={selectedGroup} onExit={onExit} data-id="element-8291" />;
    return <SessionEmptyFallback onExit={onExit} data-id="element-8292" />;
  }
  if (!currentQuestion && sessionQuestions.length > 0) {
    logError('Current question is undefined but session has questions. Index:', currentQuestionIndex);
    return <SessionSafetyError onExit={onExit} data-id="element-8293" />;
  }
  // ── Answer processing ────────────────────────────────────
  const processAnswer = (isCorrect: boolean, distractorIndex: number, errorType?: string, delta?: number, payload?: string) => {
    if (answerProcessedRef.current) return;
    answerProcessedRef.current = true;
    const timeMs = Date.now() - questionStartTimeRef.current;
    setStreakMilestone(null);
    setDailyGoalReached(false);
    // Daily Challenge Progress
    const todayChallenge = getTodayChallenge(state, MODULES, selectedGroup);
    let countsTowardsChallenge = false;
    if (todayChallenge.type === 'snelheid') {
      countsTowardsChallenge = true; // Any question counts
    } else if (todayChallenge.type === 'mix') {
      // For mix, we just count any question. The true logic of "3 different subjects"
      // is complex to track per-question, so we simplify to just answering questions in general
      // or we could check if this module is different from the last one.
      countsTowardsChallenge = true;
    } else if (todayChallenge.moduleId) {
      // For meester and herhaal, must be correct and in the right module
      countsTowardsChallenge = isCorrect && currentQuestion.module === todayChallenge.moduleId;
    }
    if (countsTowardsChallenge) {
      updateDailyChallengeProgress(1, todayChallenge.type, todayChallenge.targetCount);
    }
    if (isFluency) {
      const questionType = currentQuestion.questionType || 'multiple_choice';
      if (isCorrect && isFastResponse(questionType, timeMs)) {
        setSpeedStreak(prev => prev + 1);
      } else {
        setSpeedStreak(0);
      }
    }
    if (isChallenge) {
      if (!isCorrect) {
        challengeConsecutiveErrorsRef.current++;
        if (challengeConsecutiveErrorsRef.current >= 3 && !challengeTierDowngraded) {
          setChallengeTierDowngraded(true);
          clearTimeout(challengeTimerRef.current);
          challengeTimerRef.current = setTimeout(() => setChallengeTierDowngraded(false), SESSION_CONFIG.CHALLENGE_TIER_DOWNGRADE_MS);
        }
      } else {
        challengeConsecutiveErrorsRef.current = 0;
      }
    }
    if (!isCorrect) {
      consecutiveErrorsRef.current++;
      if (consecutiveErrorsRef.current >= 3 && !nudgeFiredRef.current && showCheatSheet) {
        setCheatSheetNudge(true);
        nudgeFiredRef.current = true;
      }
    } else {
      consecutiveErrorsRef.current = 0;
    }
    // ── P-12: Per-family frustration detection ──────────────
    // Track consecutive errors per family. When a family hits the
    // threshold, swap remaining questions from that family out of
    // the session to prevent frustration spirals.
    const frustratedFamilyId = currentQuestion.familyId;
    if (frustratedFamilyId) {
      if (!isCorrect) {
        const streak = (familyErrorStreaksRef.current.get(frustratedFamilyId) || 0) + 1;
        familyErrorStreaksRef.current.set(frustratedFamilyId, streak);
        if (streak >= V11_CONFIG.FRUSTRATION_CONSECUTIVE_ERRORS) {
          // Find remaining (future) questions from this family
          const futureIndices: number[] = [];
          for (let fi = currentQuestionIndex + 1; fi < sessionQuestions.length; fi++) {
            if (sessionQuestions[fi].familyId === frustratedFamilyId) {
              futureIndices.push(fi);
            }
          }
          if (futureIndices.length > 0) {
            // Build replacement pool: same module, different family,
            // within allowed tiers, passing pedagogical guard
            const usedIds = new Set(sessionQuestions.map(q => q.id));
            const allowedTiers = getAllowedTiersForGroup(selectedGroup);
            const allModuleQuestions = getAllQuestions().filter(q => q.module === currentQuestion.module && q.familyId !== frustratedFamilyId && !usedIds.has(q.id) && allowedTiers.has(q.tier) && evaluateQuestion(q as Question).ok);
            // Respect per-family cap: count how many questions each family
            // already has in the session to avoid over-representation
            const familyCounts = new Map<string, number>();
            for (const sq of sessionQuestions) {
              if (sq.familyId) {
                familyCounts.set(sq.familyId, (familyCounts.get(sq.familyId) || 0) + 1);
              }
            }
            const replacementPool = allModuleQuestions.filter(q => {
              const count = familyCounts.get(q.familyId!) || 0;
              return count < V11_CONFIG.MAX_QUESTIONS_PER_FAMILY;
            });
            const updated = [...sessionQuestions];
            let replacedCount = 0;
            for (const idx of futureIndices) {
              if (replacementPool.length > 0) {
                const replacement = replacementPool.shift()!;
                updated[idx] = replacement;
                usedIds.add(replacement.id);
                // Update family count for the replacement
                familyCounts.set(replacement.familyId!, (familyCounts.get(replacement.familyId!) || 0) + 1);
                replacedCount++;
              }
              // If no replacement available, keep the original (better than shortening)
            }
            if (replacedCount > 0) {
              setSessionQuestions(updated);
              debug(`[FrustrationDetection] Family ${frustratedFamilyId}: ${streak} consecutive errors → swapped ${replacedCount}/${futureIndices.length} remaining questions`);
            }
          }
        }
      } else {
        // Reset streak on correct answer for this family
        familyErrorStreaksRef.current.set(frustratedFamilyId, 0);
      }
    }
    // ── P-13: Mid-session difficulty adjustment ──────────────
    // Check running accuracy over the last N answers. If the learner
    // is performing very well, swap upcoming questions for harder ones.
    // If performing poorly, swap for easier ones. This creates a
    // "flow channel" keeping the learner in their zone of proximal
    // development. Complementary to P-12 (family swap on frustration).
    const diffWindow = V11_CONFIG.DIFFICULTY_ADJUST_WINDOW;
    const updatedAnswers = [...sessionAnswers, {
      questionId: currentQuestion.id,
      isCorrect,
      familyId: currentQuestion.familyId,
      questionType: currentQuestion.questionType || 'multiple_choice'
    }];
    if (updatedAnswers.length >= diffWindow && currentQuestionIndex - lastDifficultyAdjustIndexRef.current >= V11_CONFIG.DIFFICULTY_ADJUST_COOLDOWN) {
      const recentAnswers = updatedAnswers.slice(-diffWindow);
      const accuracy = recentAnswers.filter(a => a.isCorrect).length / diffWindow;
      const upThreshold = V11_CONFIG.DIFFICULTY_ADJUST_UP_THRESHOLD;
      const downThreshold = V11_CONFIG.DIFFICULTY_ADJUST_DOWN_THRESHOLD;
      let tierShiftDirection: 'up' | 'down' | null = null;
      if (upThreshold > 0 && accuracy >= upThreshold) {
        tierShiftDirection = 'up';
      } else if (downThreshold > 0 && accuracy <= downThreshold) {
        tierShiftDirection = 'down';
      }
      if (tierShiftDirection) {
        const shift = V11_CONFIG.DIFFICULTY_ADJUST_TIER_SHIFT * (tierShiftDirection === 'up' ? 1 : -1);
        const allowedTiers = getAllowedTiersForGroup(selectedGroup);
        const usedIds = new Set(sessionQuestions.map(q => q.id));
        const allModuleQuestions = getAllQuestions().filter(q => q.module === currentQuestion.module && !usedIds.has(q.id) && allowedTiers.has(q.tier) && evaluateQuestion(q as Question).ok);
        const updated = [...sessionQuestions];
        let swappedCount = 0;
        for (let fi = currentQuestionIndex + 1; fi < updated.length; fi++) {
          const orig = updated[fi];
          const targetTier = orig.tier + shift;
          // Find a replacement at the target tier (or nearby)
          const replacementIdx = allModuleQuestions.findIndex(q => q.tier === targetTier && !usedIds.has(q.id));
          if (replacementIdx !== -1) {
            const replacement = allModuleQuestions.splice(replacementIdx, 1)[0];
            updated[fi] = replacement;
            usedIds.add(replacement.id);
            swappedCount++;
          }
        }
        if (swappedCount > 0) {
          setSessionQuestions(updated);
          lastDifficultyAdjustIndexRef.current = currentQuestionIndex;
          debug(`[DifficultyAdjust] accuracy=${(accuracy * 100).toFixed(0)}% over last ${diffWindow} → shifted ${tierShiftDirection} by ${V11_CONFIG.DIFFICULTY_ADJUST_TIER_SHIFT} tier(s), swapped ${swappedCount} upcoming questions`);
        }
      }
    }
    const earned = calculateXP(isCorrect, currentQuestion.tier);
    setXpGained(earned);
    setTotalXpGained(prev => prev + earned);
    const questionsAnsweredToday = (state.dailyStreak?.todayQuestionsAnswered || 0) + 1;
    const dailyGoal = state.dailyStreak?.dailyGoalTarget || SESSION_CONFIG.DAILY_GOAL;
    if (questionsAnsweredToday === dailyGoal) setDailyGoalReached(true);
    if (isCorrect) {
      const newStreak = currentStreak + 1;
      setCurrentStreak(newStreak);
      const message = getStreakMessage(newStreak);
      if (message) setStreakMilestone({
        count: newStreak,
        message
      });
    } else {
      setCurrentStreak(0);
    }
    setMotivationalText(getMotivationalMessage({
      isCorrect,
      streak: isCorrect ? currentStreak + 1 : 0,
      questionNumber: currentQuestionIndex + 1,
      totalQuestions: sessionQuestions.length
    }));
    setUserAnswerIndex(distractorIndex);
    onAnswer(currentQuestion.id, isCorrect, distractorIndex, payload, errorType, delta, timeMs);
    setSessionAnswers(prev => [...prev, {
      questionId: currentQuestion.id,
      isCorrect,
      familyId: currentQuestion.familyId,
      questionType: currentQuestion.questionType || 'multiple_choice',
      errorType,
      timeMs
    }]);
    setSelectedOption(null);
    if (isFluency && isCorrect) {
      setPhase('fluency-correct');
      autoAdvanceTimerRef.current = setTimeout(() => nextQuestion(), SESSION_CONFIG.FLUENCY_AUTO_ADVANCE_MS);
    } else if (isFluency && !isCorrect) {
      setPhase('fluency-incorrect');
    } else {
      setPhase('feedback');
    }
  };
  const handleInteractiveComplete = (userAnswer: string) => {
    setUserTypedAnswer(userAnswer);
    const config = getQuestionTypeConfig(currentQuestion.questionType || 'multiple_choice');
    const validation = config.validateAnswer(userAnswer, currentQuestion);
    if (validation.displayAnswer) setUserTypedAnswer(validation.displayAnswer);
    setActualIsCorrect(validation.isCorrect);
    processAnswer(validation.isCorrect, validation.distractorIndex ?? 0, validation.errorType, validation.delta, userAnswer);
  };
  const handleRendererAnswer = (userAnswer: string | number) => {
    if (isInteractiveQuestion(currentQuestion)) handleInteractiveComplete(userAnswer as string);else {
      setUserAnswerIndex(userAnswer as number);
      setSelectedOption(userAnswer as number);
    }
  };
  const submitAnswer = () => {
    if (isInteractiveQuestion(currentQuestion)) {
      setShouldValidateInteractive(true);
      return;
    }
    if (userAnswerIndex === null) return;
    processAnswer(userAnswerIndex === currentQuestion.correctAnswer, userAnswerIndex);
  };
  // Keep ref in sync with latest submitAnswer
  submitAnswerRef.current = submitAnswer;
  const nextQuestion = () => {
    clearTimeout(autoAdvanceTimerRef.current);
    if (onClearPendingLevelUp && pendingLevelUp) onClearPendingLevelUp();
    if (isLastQuestion) {
      setPhase('summary');
    } else {
      setCurrentQuestionIndex(prev => prev + 1);
      setSelectedOption(null);
      setUserAnswerIndex(null);
      setUserTypedAnswer('');
      setActualIsCorrect(null);
      setInteractiveReady(false);
      setShouldValidateInteractive(false);
      setPhase('question');
    }
  };
  const handleCheatSheetClose = () => {
    setIsCheatSheetOpen(false);
    if (!cheatSheetViewed) onCheatSheetView();
  };
  // ── Summary phase ────────────────────────────────────────
  if (phase === 'summary') {
    return <SessionSummary moduleId={moduleId ?? '' as ModuleId} sessionQuestions={sessionQuestions} sessionAnswers={sessionAnswers} preFamilyMastery={preFamilyMastery} isFluency={isFluency} labMode={labMode} onExit={() => onComplete({
      answeredCount: sessionQuestions.length
    })} onPracticeAgain={resetSession} xpGained={totalXpGained} data-id="element-8294" />;
  }
  if (!currentQuestion) return <SessionLoadingState data-id="element-8295" />;
  // Compute module color once for the entire session
  const sessionModuleColor = moduleId ? getModuleHexColor(MODULES.find(m => m.id === moduleId)?.color || 'text-indigo-500') : '#6366F1';
  // ── Main render ──────────────────────────────────────────
  return <div className="min-h-screen bg-slate-50 flex flex-col font-sans" data-id="element-8296">
      {showCheatSheet && <CheatSheetDrawer isOpen={isCheatSheetOpen} onClose={handleCheatSheetClose} moduleId={moduleId!} subSkill={currentQuestion.subSkill} familyId={currentQuestion.familyId} data-id="element-8297" />}

      <PracticeHeaderMinimal currentQuestion={currentQuestionIndex + 1} totalQuestions={sessionQuestions.length} streak={showStreak ? state.streak?.currentTotalStreak || 0 : undefined} speedStreak={speedStreak} isFluency={isFluency} elapsedMs={isFluency && phase === 'question' ? elapsedMs : undefined} moduleColor={sessionModuleColor} onExit={onExit} onToggleCheatSheet={showCheatSheet ? () => {
      setIsCheatSheetOpen(true);
      setCheatSheetNudge(false);
    } : undefined} isCheatSheetOpen={isCheatSheetOpen} nudgeActive={cheatSheetNudge} learningGoal={currentQuestion ? getLearningGoal(currentQuestion.familyId) : undefined} data-id="element-8298" />

      <div className={`flex-1 max-w-4xl mx-auto w-full px-3 sm:px-4 md:px-6 py-3 sm:py-4 md:py-6 flex flex-col min-h-[calc(100vh-64px)] justify-center ${isFluency ? 'bg-amber-50/20' : ''}`} data-id="element-8299">
        {/* Challenge tier-downgrade encouragement banner */}
        <AnimatePresence data-id="element-8300">
          {challengeTierDowngraded && isChallenge && <motion.div key="challenge-downgrade" initial={{
          opacity: 0,
          y: -20,
          scale: 0.95
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -20,
          scale: 0.95
        }} transition={{
          type: 'spring',
          stiffness: 350,
          damping: 30
        }} className="mb-4 flex items-center gap-3" data-id="element-8301">
              <motion.div animate={{
            y: [0, -3, 0]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="flex-shrink-0" data-id="element-8302">
                <DjayAvatar size="md" emotion="pointing" variant={selectedVariant} animate data-id="element-8303" />
              </motion.div>
              <div className="relative flex-1 max-w-[320px]" data-id="element-8304">
                <div className="bg-violet-50 rounded-2xl shadow-lg border border-violet-200 px-4 py-3" data-id="element-8305">
                  <p className="text-violet-900 font-bold text-sm leading-relaxed" data-id="element-8306">
                    Lastige vragen! 💪
                  </p>
                  <p className="text-violet-700 text-sm mt-1" data-id="element-8307">
                    Even terug naar de basis — dan bouwen we weer op.
                  </p>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {/* Combo Indicator */}
        {currentStreak >= 2 && <div className="flex justify-center mb-4" data-id="element-8308">
            <ComboIndicator combo={currentStreak} data-id="element-8309" />
          </div>}

        <div className="flex-1 flex flex-col relative" data-id="element-8310">
          <AnimatePresence mode="wait" data-id="element-8311">
            {phase === 'question' && <motion.div key={currentQuestion.id} initial={{
            opacity: 0,
            x: 60,
            scale: 0.97
          }} animate={{
            opacity: 1,
            x: 0,
            scale: 1
          }} exit={{
            opacity: 0,
            x: -60,
            scale: 0.97
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 30
          }} className="w-full" data-id="element-8312">
                <div data-id="element-8313">
                  <QuestionCardClean question={currentQuestion} isFluency={isFluency} moduleColor={sessionModuleColor} data-id="element-8314">
                    <QuestionRendererFactory question={currentQuestion} onAnswer={handleRendererAnswer} onReadyChange={setInteractiveReady} shouldValidate={shouldValidateInteractive} moduleColor={sessionModuleColor} onSkipQuestion={nextQuestion} data-id="element-8315" />
                  </QuestionCardClean>
                </div>
                <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              delay: 0.2
            }} className="mt-4 sm:mt-6 md:mt-8 pb-4 flex justify-center" data-id="element-8316">
                  <motion.div className="w-full sm:w-auto rounded-2xl" animate={!(isInteractiveQuestion(currentQuestion) ? !interactiveReady : userAnswerIndex === null) ? {
                boxShadow: ['0 0 0 0px rgba(0, 0, 0, 0)', `0 0 0 8px ${sessionModuleColor}26`, '0 0 0 0px rgba(0, 0, 0, 0)']
              } : {}} transition={{
                duration: 2,
                repeat: Infinity
              }} data-id="element-8317">
                    <Button size="xl" disabled={isInteractiveQuestion(currentQuestion) ? !interactiveReady : userAnswerIndex === null} onClick={submitAnswer} className="w-full sm:w-auto sm:min-w-[280px] md:min-w-[360px] shadow-xl hover:shadow-2xl transition-all hover:-translate-y-1 rounded-2xl text-fluid-lg" data-id="element-8318">
                      <span className="mr-2 font-black" data-id="element-8319">Controleren</span>
                      <ArrowRight className="w-5 h-5" strokeWidth={3} data-id="element-8320" />
                    </Button>
                  </motion.div>
                </motion.div>
              </motion.div>}

            {phase === 'fluency-correct' && <FluencyCorrectState currentQuestionIndex={currentQuestionIndex} speedStreak={speedStreak} xpGained={xpGained} selectedVariant={selectedVariant} data-id="element-8321" />}

            {phase === 'fluency-incorrect' && <FluencyIncorrectState currentQuestionIndex={currentQuestionIndex} currentQuestion={currentQuestion} userTypedAnswer={userTypedAnswer} userAnswerIndex={userAnswerIndex} selectedVariant={selectedVariant} onShowFullFeedback={() => setPhase('feedback')} onNext={nextQuestion} data-id="element-8322" />}

            {phase === 'feedback' && showFeedback && <motion.div key={`feedback-${currentQuestionIndex}`} initial={{
            opacity: 0,
            y: 24,
            scale: 0.97
          }} animate={{
            opacity: 1,
            y: 0,
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 350,
            damping: 30
          }} className="w-full" data-id="element-8323">
                <FeedbackDisplay question={currentQuestion} wasCorrect={isInteractiveQuestion(currentQuestion) ? actualIsCorrect === true : userAnswerIndex !== null && userAnswerIndex === currentQuestion.correctAnswer} userAnswer={isInteractiveQuestion(currentQuestion) ? userTypedAnswer : userAnswerIndex !== null ? currentQuestion.options?.[userAnswerIndex] ?? '' : ''} userAnswerIndex={userAnswerIndex ?? undefined} motivationalText={motivationalText} xpGained={xpGained} onNext={nextQuestion} isLastQuestion={isLastQuestion} mode={isFluency ? 'compact' : 'full'} streak={showStreak ? state.streak?.currentTotalStreak || 0 : undefined} levelUp={pendingLevelUp ? {
              level: pendingLevelUp,
              xp,
              title: getLevelTitle(pendingLevelUp)
            } : null} streakMilestone={streakMilestone} dailyGoalReached={dailyGoalReached} onClearLevelUp={onClearPendingLevelUp} data-id="element-8324" />
              </motion.div>}
          </AnimatePresence>
        </div>

        <ExitConfirmModal show={showExitConfirm} currentIndex={currentQuestionIndex} totalQuestions={sessionQuestions.length} onContinue={() => setShowExitConfirm(false)} onStop={() => {
        setShowExitConfirm(false);
        onExit();
      }} data-id="element-8325" />
      </div>
    </div>;
}